import React, { useEffect, useRef, useState } from "react";
import { IoChevronDown } from "react-icons/io5";
import { Modal3 } from "../../../../components/DIsplay/Modal/Modal3";
import AssignRefund from "../../../../components/order/Refund/AssignRefund";
import Button1 from "../../../../components/form/Button1";
import { enqueueSnackbar } from "notistack";
import approveRefundTicket from "../../../../controllers/booking/postTicketing/refund/approveRefundTicket";
import RefundDeclineModal from "../../../../components/order/Refund/RefundDeclineModal";
import checkRefundPrice from "../../../../controllers/booking/postTicketing/refund/checkRefundPrice";
import checkRefundStatus from "../../../../controllers/booking/postTicketing/refund/checkRefundStatus";
import refundTicketRequest from "../../../../controllers/booking/postTicketing/refund/refundTicketRequest";
import manualRefund from "../../../../controllers/booking/postTicketing/refund/manualRefund";
import RefundQuote from "../../../../components/order/Refund/RefundQuote";
import ApproveRefundQuoteModal from "../../../../components/order/Refund/ApproveRefundQuoteModal";
import AddRefundRemark from "../../../../components/order/Refund/AddRefundRemark";
import RefundMessagesModal from "./RefundMessagesModal";
import SupplierResponseModal from "../../../../components/order/Void/SupplierResponseView";
import submitRefund from "../../../../controllers/booking/postTicketing/refund/submitRefund";
import ManualRefund from "../../../../components/order/Refund/ManualRefund";
import RefundNextStepCard from "./RefundNextStepCard";
import RefundReviewModal from "./RefundReviewModal";
import RefundReviewCard from "./RefundReviewCard";
import StaffAsyncActionModal from "../../../../components/order/StaffAsyncActionModal";
import StaffApiErrorModal, {
  getStaffApiErrorCopy,
} from "../../../../components/order/StaffApiErrorModal";
import {
  REFUND_ACTION,
  canEnterRefundQuote,
  getRefundAdminGuidance,
  getRefundOverflowMenuActions,
  getRefundReimburseCopy,
  getRefundReimburseWarningCopy,
  getRefundConfirmCopy,
  getRefundConfirmWarningCopy,
  getRefundSupplierCheckCopy,
  getRefundSubmitActionLabel,
  getRefundPriceFailedCopy,
  getRefundRetryAutoActionLabel,
  getRefundRetryAutoConfirmLabel,
  getRefundRetryAutoCopy,
  getRefundRetryAutoLoadingTitle,
  getRefundRetryAutoWarningCopy,
  isRefundNotApprovedError,
  needsRefundApproval,
} from "../../../../data/flight/order/getRefundAdminGuidance";

function RefundConfirmationManageOrder({ data, showBanner = true }) {
  const [manageMenu, setManageMenu] = useState(false);
  const menuRef = useRef(null);
  const buttonRef = useRef(null);
  const [modal, setModal] = useState({
    assign: false,
    accept: false,
    decline: false,
    quote: false,
    approveQuote: false,
    submission: false,
    reimburse: false,
    autoQuote: false,
    review: false,
    checkStatus: false,
    retryAuto: false,
    remark: false,
    messages: false,
    supplierResponse: false,
  });
  const [loading, setLoading] = useState({
    accept: false,
    decline: false,
    autoQuote: false,
    submission: false,
    checkStatus: false,
    retryAuto: false,
  });
  const [autoQuoteResult, setAutoQuoteResult] = useState({
    phase: "loading",
    title: "",
    text: "",
    steps: [],
  });
  const [checkStatusResult, setCheckStatusResult] = useState({
    phase: "loading",
    kind: "",
    title: "",
    text: "",
  });
  const [retryAutoResult, setRetryAutoResult] = useState({
    phase: "loading",
    kind: "",
    title: "",
    text: "",
  });
  const lastCheckResultRef = useRef(null);
  const [apiError, setApiError] = useState(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        manageMenu &&
        !menuRef.current?.contains(event.target) &&
        !buttonRef.current?.contains(event.target)
      ) {
        setManageMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [manageMenu]);

  const og = {
    ...data,
    flightBooking: [data?.flightBooking],
    flightObj: data?.flightBooking,
    type: "Flight",
  };

  const guidance = getRefundAdminGuidance(data);
  const quoted = Boolean(
    data?.passengerPrice?.length ||
      data?.priceReceived ||
      data?.isQuoted,
  );
  const canQuote = canEnterRefundQuote(data);
  const needsApprove = needsRefundApproval(data);
  const reviewNeeded = [
    "needs_review",
    "needs_manual_review",
    "send_failed",
    "validation_failed",
  ].includes(guidance.stage);

  const menuActionMap = {
    [REFUND_ACTION.review]: () => setModal({ ...modal, review: true }),
    [REFUND_ACTION.assign]: () => setModal({ ...modal, assign: true }),
    [REFUND_ACTION.decline]: () =>
      needsApprove
        ? setModal({ ...modal, review: true })
        : setModal({ ...modal, decline: true }),
    [REFUND_ACTION.autoQuote]: () => startGetAirlinePrice(),
    [REFUND_ACTION.quote]: () =>
      needsApprove
        ? setModal({ ...modal, review: true })
        : setModal({ ...modal, quote: true }),
    [REFUND_ACTION.approveQuote]: () =>
      setModal({ ...modal, approveQuote: true }),
    [REFUND_ACTION.submit]: () => setModal({ ...modal, submission: true }),
    [REFUND_ACTION.retryAuto]: () => promptRetryAutoRefund(),
    [REFUND_ACTION.checkStatus]: () => startCheckSupplierStatus(),
    [REFUND_ACTION.reimburse]: () => setModal({ ...modal, reimburse: true }),
    [REFUND_ACTION.remark]: () => setModal({ ...modal, remark: true }),
    [REFUND_ACTION.messages]: () => setModal({ ...modal, messages: true }),
    [REFUND_ACTION.supplierResponse]: () =>
      setModal({ ...modal, supplierResponse: true }),
  };

  const menuItems = getRefundOverflowMenuActions(data)
    .map((action) => ({
      label: action.label,
      action: menuActionMap[action.key],
    }))
    .filter((item) => item.action);

  function startGetAirlinePrice() {
    setManageMenu(false);
    setModal((prev) => ({ ...prev, autoQuote: true, quote: false }));
    runGetAirlinePrice();
  }

  async function runGetAirlinePrice() {
    setAutoQuoteResult({
      phase: "loading",
      title: "",
      text: "",
      steps: [],
    });
    setLoading((prev) => ({ ...prev, autoQuote: true }));
    const res = await checkRefundPrice(data?._id).finally(() =>
      setLoading((prev) => ({ ...prev, autoQuote: false })),
    );

    if (res.return && res?.data?.priceReceived) {
      setAutoQuoteResult({
        phase: "success",
        title: "We got a price",
        text: canEnterRefundQuote(data)
          ? "Open the quote, check the numbers, and send it to the agent. Do not pay the agent yet."
          : "Accept this request next, then enter the quote.",
        steps: [],
      });
      return;
    }

    const failed = getRefundPriceFailedCopy(res, {
      canQuote: canEnterRefundQuote(data),
    });
    setAutoQuoteResult({
      phase: "error",
      title: failed.title,
      text: failed.text,
      steps: failed.steps,
    });
  }

  function startCheckSupplierStatus() {
    setManageMenu(false);
    setModal((prev) => ({ ...prev, checkStatus: true }));
    runCheckSupplierStatus();
  }

  async function runCheckSupplierStatus() {
    setCheckStatusResult({
      phase: "loading",
      kind: "",
      title: "",
      text: "",
    });
    setLoading((prev) => ({ ...prev, checkStatus: true }));
    const res = await checkRefundStatus(data?._id).finally(() =>
      setLoading((prev) => ({ ...prev, checkStatus: false })),
    );
    const copy = getRefundSupplierCheckCopy(res, data?.status);
    lastCheckResultRef.current = copy;
    setCheckStatusResult(copy);
  }

  function promptConfirmRefundNow() {
    lastCheckResultRef.current = checkStatusResult;
    setCheckStatusResult(getRefundConfirmWarningCopy());
  }

  function promptRetryAutoRefund() {
    setManageMenu(false);
    setRetryAutoResult(getRefundRetryAutoWarningCopy());
    setModal((prev) => ({ ...prev, retryAuto: true }));
  }

  async function runRetryAutoRefund() {
    setRetryAutoResult({
      phase: "loading",
      kind: "retry_autoing",
      title: "",
      text: "",
    });
    setLoading((prev) => ({ ...prev, retryAuto: true }));
    const res = await refundTicketRequest(data?._id).finally(() =>
      setLoading((prev) => ({ ...prev, retryAuto: false })),
    );
    setRetryAutoResult(getRefundRetryAutoCopy(res));
  }

  function getRetryAutoActions() {
    const kind = retryAutoResult.kind;
    const close = () =>
      setModal((prev) => ({ ...prev, retryAuto: false }));
    const goBack = { label: "Go back", onClick: close };
    const seeLatest = {
      label: "See the latest status",
      primary: true,
      onClick: () => window.location.reload(),
    };
    switch (kind) {
      case "retry_auto_warn":
        return [
          {
            label: getRefundRetryAutoConfirmLabel(),
            primary: true,
            onClick: () => runRetryAutoRefund(),
          },
          goBack,
        ];
      case "retry_autoed":
        return [seeLatest];
      case "retry_auto_failed":
        return [
          {
            label: "Try again",
            primary: true,
            onClick: () => runRetryAutoRefund(),
          },
        ];
      default:
        return [goBack];
    }
  }

  function goBackFromConfirmWarn() {
    if (
      lastCheckResultRef.current?.kind &&
      !["confirm_warn", "reimburse_warn"].includes(
        lastCheckResultRef.current.kind,
      )
    ) {
      setCheckStatusResult(lastCheckResultRef.current);
      return;
    }
    closeCheckStatusModal();
  }

  function promptReimburseNow() {
    lastCheckResultRef.current = checkStatusResult;
    setCheckStatusResult(getRefundReimburseWarningCopy(data));
  }

  async function runReimburseNow() {
    setCheckStatusResult({
      phase: "loading",
      kind: "reimbursing",
      title: "",
      text: "",
    });
    setLoading((prev) => ({ ...prev, checkStatus: true }));
    const res = await manualRefund(data?._id).finally(() =>
      setLoading((prev) => ({ ...prev, checkStatus: false })),
    );
    setCheckStatusResult(getRefundReimburseCopy(res));
  }

  async function runConfirmRefundNow() {
    setCheckStatusResult({
      phase: "loading",
      kind: "confirming",
      title: "",
      text: "",
    });
    setLoading((prev) => ({ ...prev, checkStatus: true }));
    const res = await refundTicketRequest(data?._id).finally(() =>
      setLoading((prev) => ({ ...prev, checkStatus: false })),
    );
    setCheckStatusResult(getRefundConfirmCopy(res));
  }

  function closeCheckStatusModal() {
    setModal((prev) => ({ ...prev, checkStatus: false }));
  }

  function getCheckStatusActions() {
    const kind = checkStatusResult.kind;
    const tryAgain = {
      label: "Try again",
      onClick: () => runCheckSupplierStatus(),
    };
    const assign = {
      label: "Assign",
      onClick: () =>
        setModal((prev) => ({ ...prev, checkStatus: false, assign: true })),
    };
    const seeLatest = {
      label: "See latest",
      primary: true,
      onClick: () => window.location.reload(),
    };
    const confirmNow = {
      label: "Confirm refund",
      onClick: () => promptConfirmRefundNow(),
    };
    const reimburseNow = {
      label: "Credit wallet",
      onClick: () => promptReimburseNow(),
    };
    const yesConfirm = {
      label: "Yes, continue",
      danger: true,
      onClick: () => runConfirmRefundNow(),
    };
    const yesReimburse = {
      label: "Yes, the ticket is refunded",
      danger: true,
      onClick: () => runReimburseNow(),
    };
    const goBack = {
      label: "Go back",
      onClick: () => goBackFromConfirmWarn(),
    };

    switch (kind) {
      case "succeeded":
        return [{ ...reimburseNow, primary: true }, seeLatest];
      case "completed":
      case "updated":
      case "confirmed":
      case "reimbursed":
        return [seeLatest];
      case "supplier_failed":
        return [reimburseNow, assign, tryAgain];
      case "confirm_warn":
        return [yesConfirm, goBack];
      case "reimburse_warn":
        return [yesReimburse, goBack];
      case "confirm_failed":
        return [{ ...reimburseNow, primary: true }, tryAgain, confirmNow];
      case "reimburse_failed":
        return [{ ...reimburseNow, primary: true }, tryAgain, confirmNow];
      case "not_found":
      case "network":
      case "generic":
      case "still_waiting":
        return [
          { ...tryAgain, primary: true },
          reimburseNow,
          confirmNow,
          assign,
        ];
      default:
        return [tryAgain];
    }
  }

  async function handleApproveRefund() {
    setLoading({ ...loading, accept: true });
    const res = await approveRefundTicket(data?._id).finally(() =>
      setLoading({ ...loading, accept: false }),
    );
    if (res.return) {
      enqueueSnackbar("Request accepted. You can create a quote now.", {
        variant: "success",
      });
      setTimeout(() => window.location.reload(), 300);
    } else setApiError(getStaffApiErrorCopy(res));
  }

  async function handleSubmitRefund() {
    setLoading({ ...loading, submission: true });
    const res = await submitRefund(data?._id).finally(() =>
      setLoading({ ...loading, submission: false }),
    );
    if (res?.return) {
      enqueueSnackbar(res?.msg || "Marked as submitted to the airline.", {
        variant: "success",
      });
      setTimeout(() => window.location.reload(), 300);
    } else
      setApiError(
        getStaffApiErrorCopy(res, "We could not mark this as sent"),
      );
  }

  function handleAction(key) {
    switch (key) {
      case REFUND_ACTION.accept:
      case REFUND_ACTION.review:
        setModal({ ...modal, review: true });
        break;
      case REFUND_ACTION.assign:
        setModal({ ...modal, assign: true });
        break;
      case REFUND_ACTION.decline:
        if (needsRefundApproval(data)) {
          setModal({ ...modal, review: true });
          break;
        }
        setModal({ ...modal, decline: true });
        break;
      case REFUND_ACTION.autoQuote:
        startGetAirlinePrice();
        break;
      case REFUND_ACTION.quote:
        if (needsRefundApproval(data)) {
          setModal({ ...modal, review: true });
          break;
        }
        setModal({ ...modal, quote: true });
        break;
      case REFUND_ACTION.approveQuote:
        setModal({ ...modal, approveQuote: true });
        break;
      case REFUND_ACTION.submit:
        setModal({ ...modal, submission: true });
        break;
      case REFUND_ACTION.retryAuto:
        promptRetryAutoRefund();
        break;
      case REFUND_ACTION.reimburse:
        setModal({ ...modal, reimburse: true });
        break;
      case REFUND_ACTION.checkStatus:
        startCheckSupplierStatus();
        break;
      case REFUND_ACTION.remark:
        setModal({ ...modal, remark: true });
        break;
      case REFUND_ACTION.messages:
        setModal({ ...modal, messages: true });
        break;
      case REFUND_ACTION.supplierResponse:
        setModal({ ...modal, supplierResponse: true });
        break;
      default:
        break;
    }
  }

  return (
    <>
      {showBanner ? (
        <RefundNextStepCard
          data={data}
          loading={{
            [REFUND_ACTION.checkStatus]: loading.checkStatus,
            [REFUND_ACTION.accept]: loading.accept,
            [REFUND_ACTION.decline]: loading.decline,
            [REFUND_ACTION.submit]: loading.submission,
          }}
          onAction={handleAction}
        />
      ) : null}
      {showBanner && reviewNeeded ? <RefundReviewCard data={data} /> : null}

      <RefundReviewModal
        open={modal.review}
        setOpen={(next) => setModal((prev) => ({ ...prev, review: next }))}
        data={data}
        onDone={() => window.location.reload()}
        onQuote={() => {
          setModal((prev) => ({ ...prev, review: false, quote: true }));
        }}
        onSubmit={() => {
          setModal((prev) => ({ ...prev, review: false, submission: true }));
        }}
        onReimburse={() => {
          setModal((prev) => ({ ...prev, review: false, reimburse: true }));
        }}
      />
      <StaffApiErrorModal
        open={Boolean(apiError)}
        setOpen={(next) => {
          if (!next) setApiError(null);
        }}
        title={apiError?.title}
        text={apiError?.text}
      />

      <StaffAsyncActionModal
        open={modal.retryAuto}
        setOpen={(next) => setModal((prev) => ({ ...prev, retryAuto: next }))}
        title={getRefundRetryAutoActionLabel()}
        phase={retryAutoResult.phase}
        loadingTitle={getRefundRetryAutoLoadingTitle()}
        loadingText="This can take a few seconds. Stay on this screen. Do not click again."
        resultTitle={retryAutoResult.title}
        resultText={retryAutoResult.text}
        actions={getRetryAutoActions()}
      />
      <StaffAsyncActionModal
        open={modal.autoQuote}
        setOpen={(next) => setModal((prev) => ({ ...prev, autoQuote: next }))}
        title="Get supplier price"
        phase={autoQuoteResult.phase}
        loadingTitle="Asking the supplier for a price"
        loadingText="This can take a few seconds. Stay on this screen. Do not click again."
        resultTitle={autoQuoteResult.title}
        resultText={autoQuoteResult.text}
        resultSteps={autoQuoteResult.steps}
        actions={
          autoQuoteResult.phase === "success"
            ? canQuote
              ? [
                  {
                    label: "See the quote",
                    primary: true,
                    onClick: () => window.location.reload(),
                  },
                ]
              : [
                  {
                    label: "Review this request",
                    primary: true,
                    onClick: () =>
                      setModal((prev) => ({
                        ...prev,
                        autoQuote: false,
                        review: true,
                      })),
                  },
                ]
            : autoQuoteResult.phase === "error"
              ? canQuote
                ? [
                    {
                      label: "Enter quote from airline",
                      primary: true,
                      onClick: () =>
                        setModal((prev) => ({
                          ...prev,
                          autoQuote: false,
                          quote: true,
                        })),
                    },
                    {
                      label: "Try again",
                      onClick: () => runGetAirlinePrice(),
                    },
                    {
                      label: "Assign",
                      onClick: () =>
                        setModal((prev) => ({
                          ...prev,
                          autoQuote: false,
                          assign: true,
                        })),
                    },
                  ]
                : [
                    {
                      label: "Review this request",
                      primary: true,
                      onClick: () =>
                        setModal((prev) => ({
                          ...prev,
                          autoQuote: false,
                          review: true,
                        })),
                    },
                    {
                      label: "Try again",
                      onClick: () => runGetAirlinePrice(),
                    },
                    {
                      label: "Assign",
                      onClick: () =>
                        setModal((prev) => ({
                          ...prev,
                          autoQuote: false,
                          assign: true,
                        })),
                    },
                  ]
              : []
        }
      />

      <StaffAsyncActionModal
        open={modal.checkStatus}
        setOpen={(next) =>
          setModal((prev) => ({ ...prev, checkStatus: Boolean(next) }))
        }
        title={
          ["confirming", "confirmed", "confirm_failed", "confirm_warn"].includes(
            checkStatusResult.kind,
          )
            ? "Confirm refund now"
            : [
                  "reimbursing",
                  "reimbursed",
                  "reimburse_failed",
                  "reimburse_warn",
                ].includes(checkStatusResult.kind)
              ? "Re-imburse / Refund"
              : "Check supplier status"
        }
        phase={checkStatusResult.phase}
        loadingTitle={
          checkStatusResult.kind === "confirming"
            ? "Confirming this refund"
            : checkStatusResult.kind === "reimbursing"
              ? "Paying the agent wallet"
              : "Asking the airline for an update"
        }
        loadingText="This can take a few seconds. Stay on this screen. Do not click again."
        resultTitle={checkStatusResult.title}
        resultText={checkStatusResult.text}
        resultSteps={checkStatusResult.steps}
        actions={getCheckStatusActions()}
      />

      <Modal3
        open={modal.reimburse}
        setOpen={(modalState) => setModal({ ...modal, reimburse: modalState })}
        title="Credit agent wallet"
      >
        <div className="p-4 max-w-[700px]">
          <ManualRefund callback={() => window.location.reload()} data={og} />
        </div>
      </Modal3>
      <Modal3
        open={modal.quote}
        setOpen={(modalState) => setModal({ ...modal, quote: modalState })}
        title={quoted ? "Edit quote from airline" : "Enter quote from airline"}
      >
        <div className="p-4 max-w-[700px] md:min-w-[500px]">
          <p className="text-sm text-gray-600 mb-3">
            Use the refund numbers from the airline or supplier site. Do not
            invent amounts. Do not submit the refund on the airline site yet.
          </p>
          <RefundQuote data={og} reload={() => window.location.reload()} />
        </div>
      </Modal3>

      <ApproveRefundQuoteModal
        open={modal.approveQuote}
        setOpen={(next) =>
          setModal((prev) => ({ ...prev, approveQuote: Boolean(next) }))
        }
        data={data}
        onDone={() => window.location.reload()}
      />

      <RefundMessagesModal
        open={modal.messages}
        setOpen={(open) => setModal({ ...modal, messages: open })}
        data={data}
      />
      <SupplierResponseModal
        open={modal.supplierResponse}
        setOpen={(open) =>
          setModal((prev) => ({ ...prev, supplierResponse: Boolean(open) }))
        }
        data={data?.supplierResponse}
        orderId={data?.orderId}
      />
      <Modal3
        open={modal.submission}
        setOpen={(modalState) => setModal({ ...modal, submission: modalState })}
        title={getRefundSubmitActionLabel()}
      >
        <div className="card p-4 flex flex-col gap-4 min-w-[300px]">
          <div className="py-4">
            Only continue if you already opened the airline or supplier site and
            submitted the refund there. This button only tells Intraverse you
            submitted it. It does not pay the agent. A timeout is not an airline
            decline.
          </div>
          <div className="flex justify-end items-center gap-4">
            <Button1
              variant="outlined"
              className="btn-outlined flex-1"
              onClick={() => setModal({ ...modal, submission: false })}
            >
              Back
            </Button1>
            <div>
              <Button1
                onClick={() => handleSubmitRefund()}
                loading={loading?.submission}
              >
                Yes, I submitted it on the airline site
              </Button1>
            </div>
          </div>
        </div>
      </Modal3>
      <Modal3
        open={modal.assign}
        setOpen={(modalState) => setModal({ ...modal, assign: modalState })}
        title="Assign refund"
      >
        <div className="p-4 max-w-[700px]">
          <AssignRefund callback={() => window.location.reload()} data={og} />
        </div>
      </Modal3>
      <Modal3
        open={modal.accept}
        setOpen={(modalState) => setModal({ ...modal, accept: modalState })}
        title="Accept refund request"
      >
        <div className="max-w-[500px] p-4">
          <div className="p-4">
            <p>
              You are saying this refund should go ahead. After Accept, you can
              get a supplier price or type a quote.
            </p>
          </div>
          <div className="flex gap-4">
            <Button1
              variant="text"
              onClick={() => setModal({ ...modal, accept: false })}
            >
              Back
            </Button1>
            <Button1
              loading={loading?.accept}
              onClick={() => handleApproveRefund()}
            >
              Accept request
            </Button1>
          </div>
        </div>
      </Modal3>
      <Modal3
        open={modal.remark}
        setOpen={(modalState) => setModal({ ...modal, remark: modalState })}
        title="Add remark"
      >
        <div className="card p-4 flex flex-col gap-4 md:min-w-[500px]">
          <AddRefundRemark
            data={data}
            close={() => setModal({ ...modal, remark: false })}
            callback={() => window.location.reload()}
          />
        </div>
      </Modal3>
      <RefundDeclineModal
        open={modal.decline}
        setOpen={(next) =>
          setModal((prev) => ({ ...prev, decline: Boolean(next) }))
        }
        data={data}
        onDone={() => window.location.reload()}
      />

      {menuItems?.length ? (
        <div className="relative w-full lg:w-fit ml-auto mt-2">
          <button
            ref={buttonRef}
            onClick={() => setManageMenu((prev) => !prev)}
            className="border border-gray-300 h-12 rounded-md bg-gray-100 hover:bg-gray-200 transition-all w-full px-2 lg:px-4 flex items-center justify-center gap-2 text-sm font-semibold"
          >
            <span>More actions</span>
            <IoChevronDown
              className={`text-base transition-transform mt-[2px] ${
                manageMenu ? "rotate-180" : ""
              }`}
            />
          </button>

          {manageMenu && (
            <div
              ref={menuRef}
              className="absolute right-0 mt-2 w-56 rounded-md border border-gray-200 bg-white shadow-lg overflow-hidden z-20"
            >
              <div className="px-3 py-2 text-xs text-gray-500 border-b border-gray-100">
                Current: {guidance.statusLabel}
              </div>
              {menuItems.map((itm, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    setManageMenu(false);
                    itm.action?.();
                  }}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                >
                  {itm.label}
                </button>
              ))}
            </div>
          )}
        </div>
      ) : null}
    </>
  );
}

export default RefundConfirmationManageOrder;
